import React, { useState, useRef, useEffect } from "react";
import { ChevronRight, Smartphone, Plus, Building2 } from "lucide-react";
import axios from "axios";

export default function ChangeOrgProfile({
  changeOrganization,
  setShowCreateNewOrganization,
}) {
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const menuRef = useRef(null);


  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get organizations from localStorage
  const getOrganizationsFromStorage = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      const orgs = userData?.relatedOrgs?.L || [];
      return orgs;
    } catch (error) {
      // Log when localStorage user data is corrupt or missing
      console.error("Error parsing user data from localStorage:", error);
      return [];
    }
  };

  const [organizations, setOrganization] = useState(() => {
    return getOrganizationsFromStorage();
  });

  // Sync organizations when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const updatedOrgs = getOrganizationsFromStorage();
      setOrganization(updatedOrgs);
    };

    // Listen for storage events (when localStorage is updated from other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom event that can be dispatched when localStorage is updated
    window.addEventListener("userDataUpdated", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userDataUpdated", handleStorageChange);
    };
  }, []);

  // Fetch fresh user data from API when dropdown opens
  useEffect(() => {
    if (open) {
      const fetchFreshUserData = async () => {
        try {
          const userId = JSON.parse(localStorage.getItem("user"))?.userId?.S;
          if (!userId) {
            const updatedOrgs = getOrganizationsFromStorage();
            setOrganization(updatedOrgs);
            return;
          }

          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
            { userId }
          );

          if (response.data?.user?.relatedOrgs?.L) {
            const freshOrgs = response.data.user.relatedOrgs.L;
            
            // Update localStorage with fresh data
            localStorage.setItem("user", JSON.stringify(response.data.user));
            
            // Update state
            setOrganization(freshOrgs);
            
            // Dispatch custom event for other components
            window.dispatchEvent(new Event("userDataUpdated"));
          } else {
            const updatedOrgs = getOrganizationsFromStorage();
            setOrganization(updatedOrgs);
          }
        } catch (error) {
          // Log for production diagnostics when org list fetch fails
          console.error('[ChangeOrgProfile] Error fetching fresh user data:', error);
          // Fallback to localStorage on error
          const updatedOrgs = getOrganizationsFromStorage();
          setOrganization(updatedOrgs);
        }
      };

      fetchFreshUserData();
    }
  }, [open]);

  // Current org from localStorage
  const orgData = JSON.parse(localStorage.getItem("organization"));
  const orgName = orgData?.organization_name?.S || "Select organization";
  const orgId = orgData?.orgId?.S;
  const displayName =
    orgName.length > 17 ? orgName.slice(0, 17) + "..." : orgName;

  return (
    <div className="relative inline-block w-full" ref={menuRef}>
      {/* Trigger */}
      <div
        className="cursor-pointer justify-between flex items-center py-2 px-3 text-[14px] font-semibold pl-6 rounded-[8px] bg-[#F9FAFB]"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="flex items-center">
          <Smartphone className="w-[16px] mr-2 text-gray-700" />
          {displayName}
        </div>
        <ChevronRight
          className={`w-[16px] ml-2 text-gray-500 transition-transform ${
            open ? "rotate-90" : ""
          }`}
        />
      </div>

      {/* Dropdown Menu (opens above) */}
      {open && (
        <div className="absolute bottom-full mb-2 left-0 w-[100%] bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-50 flex flex-col max-h-[400px]">
          {/* Scrollable organizations list */}
          <div className="overflow-y-auto flex-1">
            {organizations && organizations.length > 0 ? (
              organizations.map((org, index) => (
                <div
                  key={org?.M?.org_id?.S || index}
                  className={`flex items-center gap-2 px-4 py-2 cursor-pointer text-sm hover:bg-gray-50 ${
                    org?.M?.org_id?.S === orgId ? "bg-indigo-50 text-indigo-600" : ""
                  }`}
                  onClick={() => {
                    if (org?.M?.org_id?.S) {
                      changeOrganization(org.M.org_id.S);
                      setOpen(false);
                    }
                  }}
                >
                  <Building2 className="w-4 h-4" />
                  <span className="flex-1">{org?.M?.org_name?.S || "Unknown Organization"}</span>
                  {org?.M?.org_id?.S === orgId && (
                    <span className="text-indigo-500">✔</span>
                  )}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">
                No organizations found
              </div>
            )}
          </div>
          {/* Fixed footer with create button */}
          <div className="border-t border-gray-100 flex-shrink-0">
            <div
              className="flex items-center gap-2 px-4 py-2 text-indigo-600 hover:bg-indigo-50 cursor-pointer text-[12px]"
              onClick={() => {
                setShowCreateNewOrganization(true);
                setOpen(false);
              }}
            >
              <Plus className="w-4 h-4" />
              Create new organization
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
